import { ColDef, GridApi } from 'ag-grid-community';
import { createContext, forwardRef, useCallback, useContext, useMemo, useRef, useState } from 'react';
import DataTable, { IDataTablePublic } from 'components/tables/DataTable';
import { ADD_FORM_PAGE_PATH, ADD_REVISION_PAGE_PATH } from 'routes/paths';
import OrganizationMultiSelectFilter, {
  OrganizationMultiColumnFilter,
} from 'components/tables/filters/OrganizationMultiSelectFilter';
import useObjectColumn from 'components/tables/hierarchy/ObjectColumn';
import DeviceTypeSelectFilter from 'components/tables/filters/DeviceTypeSelectFilter';
import {
  FormStatusFloatingFilter,
  RevisionStatusFloatingFilter,
} from 'components/tables/filters/revision-status/RevisionStatusFloatingFilter';
import { useApi } from 'api/api-context';
import { getEvidenceFieldValue } from 'utils/evidence-field';
import DeviceSubtypeContainsSelectFilter from 'components/tables/filters/device-subtype/DeviceSubtypeContainsSelectFilter';
import AssignedBySelectFilter from 'components/tables/filters/AssignedBySelectFilter';
import TechnicianSelectFilter from 'components/tables/filters/TechnicianSelectFilter';
import RevisionDeadlineSelectFilter from 'components/tables/filters/RevisionDeadlineSelectFilter';
import { isAfter } from 'date-fns';
import RevisionStatusFilter from 'components/tables/filters/revision-status/RevisionStatusFilter';
import OrganizationNameColumn from 'components/tables/data/revision/columns/OrganizationNameColumn';
import RevisionIdColumn from 'components/tables/data/revision/columns/RevisionIdColumn';
import AssignedByOrganizationNameColumn from 'components/tables/data/revision/columns/AssignedByOrganizationNameColumn';
import DeviceTypeNameColumn from 'components/tables/data/revision/columns/DeviceTypeNameColumn';
import DeviceSubtypesColumn from 'components/tables/data/revision/columns/DeviceSubtypesColumn';
import RevisionStatusColumn from 'components/tables/data/revision/columns/RevisionStatusColumn';
import RevisionNameColumn from 'components/tables/data/revision/columns/RevisionNameColumn';
import TechnicianNameColumn from 'components/tables/data/revision/columns/TechnicianNameColumn';
import DeadlineColumn from 'components/tables/data/revision/columns/DeadlineColumn';
import DevicesColumn from 'components/tables/data/revision/columns/DevicesColumn';
import ActionsColumn from 'components/tables/data/revision/columns/ActionsColumn';
import {
  OnStatusChangeParams,
  OnNoLongerAccessible,
  OnStatusChange,
  RevisionRow,
} from 'components/tables/data/revision/types';
import SafetyPointColumn from 'components/tables/data/revision/columns/SafetyPointColumn';
import RevisionSafetyPointSelectFilter from 'components/tables/filters/RevisionSafetyPointSelectFilter';
import { Group } from '@mantine/core';
import Button from 'components/Button';
import { IconCooker, IconForms } from '@tabler/icons-react';
import BulkAcceptAction from 'components/tables/data/revision/actions/bulk/BulkAcceptAction';
import BulkApproveAction from 'components/tables/data/revision/actions/bulk/BulkApproveAction';
import BulkConfirmAction from 'components/tables/data/revision/actions/bulk/BulkConfirmAction';
import BulkDeleteAction from 'components/tables/data/revision/actions/bulk/BulkDeleteAction';
import BulkRejectAction from 'components/tables/data/revision/actions/bulk/BulkRejectAction';
import BulkSignAction from 'components/tables/data/revision/actions/bulk/BulkSignAction';
import BulkViewAction from 'components/tables/data/revision/actions/bulk/BulkViewAction';
import FinishedAtColumn from 'components/tables/data/revision/columns/FinishedAtColumn';
import NextRevisionDateColumn from 'components/tables/data/revision/columns/NextRevisionDateColumn';
import DocumentValidUntilColumn from 'components/tables/data/revision/columns/DocumentValidUntilColumn';
import RevisionDeviceSelectFilter from 'components/tables/filters/RevisionDeviceSelectFilter';
import RevisionTemplateColumn from 'components/tables/data/revision/columns/RevisionTemplateColumn';
import RevisionTemplateSelectFilter from 'components/tables/filters/RevisionTemplateSelectFilter';
import RemoteIdColumn from 'components/tables/data/revision/columns/RemoteIdColumn';
import RemoteSourceColumn from 'components/tables/data/revision/columns/RemoteSourceColumn';
import RemoteSourceSelectFilter from 'components/tables/filters/RemoteSourceSelectFilter';
import DateFilter, { DateColumnFilter } from 'components/tables/filters/DateFilter';
import {
  FEATURE_TOGGLE_BULK_SIGN_REVISION,
  FEATURE_TOGGLE_REMOTE_SOURCES,
  FEATURE_TOGGLE_REVISION_TABLE_EVIDENCE_FIELDS_COLUMNS,
  FEATURE_TOGGLE_REVISION_TABLE_INDEX_VALUES_COLUMNS,
  FEATURE_TOGGLE_REVISION_TABLE_VIEW_VARIANT,
} from 'env';
import { translateSafetyPoint } from 'model/RevisionSafetyPoint';
import { getKindPretty } from 'components/tables/data/kind';
import { IconClipboard } from '@tabler/icons-react';
import { getResponsiveActionsColumnWidth, useResponsiveDesign } from 'hooks/use-responsive-design';
import SimpleValueFormatter from 'components/tables/SimpleValueFormatter';
import RevisionFormTypeSelectFilter from 'components/tables/filters/RevisionFormTypeSelectFilter';
import FormTypeColumn from 'components/tables/data/revision/columns/FormTypeColumn';
import useLocalStorage from 'hooks/use-local-storage';

interface RevisionTableProps {
  kind: 'revision' | 'form';
  suppressSaveProfiles?: boolean;
  suppressProfileChangedNotification?: boolean;
  initialFilters?: Record<string, any>;
}

/**
 * Table which displays revisions.
 */
const RevisionTableImpl = forwardRef<IDataTablePublic, RevisionTableProps>(
  ({ kind, initialFilters, suppressProfileChangedNotification, suppressSaveProfiles }, ref) => {
    const { isTablet, tableActionMode } = useResponsiveDesign();
    const { getAction, hasPermissionAnywhere, userId } = useApi();
    const [selectedRows, setSelectedRows] = useState<RevisionRow[]>([]);
    const [api, setApi] = useState<GridApi<RevisionRow> | null>(null);
    const [{ viewVariant }, setViewVariant] = useLocalStorage<{ viewVariant: 'view-by-revision' | 'view-by-device' }>(
      `objekto.revision-table-${kind}.view-variant`,
      { viewVariant: 'view-by-revision' }
    );
    const columnInventoryScope = `${kind}:${viewVariant}:${userId}`;
    const [loadedColumnInventoryScope, setLoadedColumnInventoryScope] = useState<string | null>(null);
    const columnInventoryRequest = useRef(0);
    const [indexValuesColumns, setIndexValuesColumns] = useState<ColDef[]>([]);
    const [deviceEvidenceFieldsColumns, setDeviceEvidenceFieldsColumns] = useState<ColDef[]>([]);
    const objectColumn = useObjectColumn({
      breadcrumbField: 'department.breadcrumbs',
      organizationFilterKey: 'organization.organizationId',
      legacyColumnIds: ['department.departmentName'],
    });

    const viewToggle = useMemo(() => {
      if (!FEATURE_TOGGLE_REVISION_TABLE_VIEW_VARIANT) {
        return null;
      }

      const revisionText = kind === 'revision' ? 'Zobraziť podľa revízie' : 'Zobraziť podľa formulára';
      const revisionIcon = kind === 'revision' ? <IconClipboard /> : <IconForms />;

      return (
        <Button
          size={isTablet ? 'sm' : 'md'}
          variant="tertiary"
          leftIcon={viewVariant === 'view-by-revision' ? <IconCooker /> : revisionIcon}
          onClick={() =>
            setViewVariant((prev) => ({
              ...prev,
              viewVariant: prev.viewVariant === 'view-by-revision' ? 'view-by-device' : 'view-by-revision',
            }))
          }
        >
          {viewVariant === 'view-by-revision' ? 'Zobraziť podľa zariadenia' : revisionText}
        </Button>
      );
    }, [kind, viewVariant, isTablet]);

    /**
     * The columns of the table.
     */
    const columns: ColDef[] = useMemo(
      () =>
        (
          [
            {
              field: 'revisionId',
              headerName: 'ID',
              sortable: true,
              unSortIcon: true,
              width: 76,
              minWidth: 76,
              maxWidth: 76,
              resizable: false,
              cellRenderer: RevisionIdColumn,
            },
            kind === 'form'
              ? {
                  field: 'kindPretty',
                  headerName: 'Typ formulára',
                  minWidth: 240,
                  sortable: true,
                  unSortIcon: true,
                  filter: true,
                  floatingFilter: true,
                  floatingFilterComponent: RevisionFormTypeSelectFilter,
                  cellRenderer: FormTypeColumn,
                }
              : null,
            kind === 'form'
              ? {
                  field: 'kind',
                  headerName: 'Typ formulára',
                  filter: true,
                  hide: true,
                }
              : null,
            {
              field: 'organization.organizationId',
              hide: true,
              filter: OrganizationMultiColumnFilter,
              headerName: 'ID organizácie',
            },
            {
              field: 'organization.organizationName',
              headerName: 'Organizácia',
              minWidth: 225,
              filter: true,
              floatingFilter: true,
              floatingFilterComponent: OrganizationMultiSelectFilter,
              sortable: true,
              unSortIcon: true,
              cellRenderer: OrganizationNameColumn,
            },
            ...objectColumn.columns,
            {
              field: 'assignedBy.organizationId',
              hide: true,
              filter: true,
              headerName: 'ID organizácie zadávateľa',
            },
            {
              field: 'assignedBy.organizationName',
              headerName: 'Zadávateľ',
              minWidth: 200,
              filter: true,
              floatingFilter: true,
              floatingFilterComponent: AssignedBySelectFilter,
              sortable: true,
              unSortIcon: true,
              cellRenderer: AssignedByOrganizationNameColumn,
            },
            {
              field: 'deviceType.deviceTypeId',
              hide: true,
              filter: true,
              headerName: 'ID typu zariadenia',
            },
            {
              field: 'deviceType.deviceTypeName',
              headerName: 'Zariadenie',
              minWidth: 220,
              filter: true,
              floatingFilter: true,
              floatingFilterComponent: DeviceTypeSelectFilter,
              sortable: true,
              unSortIcon: true,
              cellRenderer: DeviceTypeNameColumn,
            },
            {
              field: 'deviceSubtypes.deviceTypeId',
              hide: true,
              filter: true,
            },
            {
              field: 'deviceSubtypes.deviceTypeName',
              hide: true,
              filter: true,
            },
            {
              field: 'deviceSubtypesString',
              hide: true,
              headerName: 'Typ zariadenia',
            },
            {
              field: 'deviceSubtypes',
              headerName: 'Typ zariadenia',
              minWidth: 220,
              filter: true,
              floatingFilter: true,
              floatingFilterComponent: DeviceSubtypeContainsSelectFilter,
              sortable: true,
              unSortIcon: true,
              valueGetter: ({ data }: { data: RevisionRow }) =>
                data.deviceSubtype.map(({ deviceTypeName }) => deviceTypeName).join(', '),
              cellRenderer: DeviceSubtypesColumn,
            },
            FEATURE_TOGGLE_REMOTE_SOURCES
              ? {
                  field: 'remoteSource.remoteSourceId',
                  hide: true,
                  filter: true,
                  headerName: 'ID externého zdroja',
                }
              : null,
            FEATURE_TOGGLE_REMOTE_SOURCES
              ? {
                  field: 'remoteSource.name',
                  headerName: 'Externý zdroj',
                  minWidth: 200,
                  filter: true,
                  floatingFilter: true,
                  floatingFilterComponent: RemoteSourceSelectFilter,
                  sortable: true,
                  unSortIcon: true,
                  cellRenderer: RemoteSourceColumn,
                }
              : null,
            FEATURE_TOGGLE_REMOTE_SOURCES
              ? {
                  field: 'remoteId',
                  headerName: 'Externé ID',
                  minWidth: 160,
                  cellRenderer: RemoteIdColumn,
                }
              : null,
            FEATURE_TOGGLE_REMOTE_SOURCES
              ? {
                  field: 'remoteUrl',
                  headerName: 'Externé URL',
                  hide: true,
                }
              : null,
            {
              field: 'revisionStatus.revisionStatusName',
              headerName: kind === 'revision' ? 'Stav revíznej správy' : 'Stav formulára',
              filter: true,
              hide: true,
            },
            {
              field: 'revisionStatus.revisionStatusId',
              headerName: 'Stav',
              minWidth: 192,
              sortable: true,
              unSortIcon: true,
              filter: RevisionStatusFilter,
              floatingFilter: true,
              floatingFilterComponent: kind === 'revision' ? RevisionStatusFloatingFilter : FormStatusFloatingFilter,
              cellRenderer: RevisionStatusColumn,
            },
            {
              field: 'revisionName',
              headerName: 'Názov',
              minWidth: 250,
              sortable: true,
              unSortIcon: true,
              cellRenderer: RevisionNameColumn,
            },
            {
              field: 'revisionTemplate.slug',
              headerName: 'Šablóna',
              minWidth: 250,
              sortable: true,
              unSortIcon: true,
              filter: true,
              floatingFilter: true,
              floatingFilterComponent: RevisionTemplateSelectFilter,
              cellRenderer: RevisionTemplateColumn,
            },
            {
              field: 'technician.userId',
              hide: true,
              filter: true,
              headerName: kind === 'revision' ? 'ID revízneho technika' : 'ID vypracovateľa',
            },
            {
              field: 'technician.fullName',
              headerName: kind === 'revision' ? 'Revízny technik' : 'Vypracovateľ',
              minWidth: 180,
              sortable: true,
              unSortIcon: true,
              filter: true,
              floatingFilter: true,
              floatingFilterComponent: TechnicianSelectFilter,
              cellRenderer: TechnicianNameColumn,
            },
            {
              field: 'deadlineStatus',
              hide: true,
              filter: true,
            },
            {
              field: 'deadline',
              headerName: 'Deadline',
              minWidth: 170,
              sortable: true,
              unSortIcon: true,
              filter: true,
              floatingFilter: true,
              floatingFilterComponent: RevisionDeadlineSelectFilter,
              cellRenderer: DeadlineColumn,
            },
            {
              field: 'safetyPoint',
              hide: true,
              filter: true,
            },
            {
              field: 'safetyPointLabel',
              hide: true,
              filter: true,
            },
            {
              field: 'safetyPointPretty',
              headerName: 'Záver',
              minWidth: 180,
              sortable: true,
              unSortIcon: true,
              filter: true,
              floatingFilter: true,
              floatingFilterComponent: RevisionSafetyPointSelectFilter,
              cellRenderer: SafetyPointColumn,
            },
            {
              field: 'finishedAt',
              headerName: 'Dátum ukončenia',
              filter: DateColumnFilter,
              floatingFilter: true,
              floatingFilterComponent: DateFilter.configure({ filterKey: 'finishedAt' }),
              minWidth: 200,
              sortable: true,
              unSortIcon: true,
              cellRenderer: FinishedAtColumn,
            },
            {
              field: 'nextRevisionDate',
              headerName:
                kind === 'revision' ? 'Dátum nasledujúcej revíznej správy' : 'Dátum nasledujúceho vypracovania',
              filter: DateColumnFilter,
              floatingFilter: true,
              floatingFilterComponent: DateFilter.configure({ filterKey: 'nextRevisionDate' }),
              minWidth: 220,
              sortable: true,
              unSortIcon: true,
              cellRenderer: NextRevisionDateColumn,
            },
            {
              field: 'documentValidUntil',
              headerName: 'Deadline na prepečiatkovanie',
              filter: DateColumnFilter,
              floatingFilter: true,
              floatingFilterComponent: DateFilter.configure({ filterKey: 'documentValidUntil' }),
              minWidth: 200,
              sortable: true,
              unSortIcon: true,
              cellRenderer: DocumentValidUntilColumn,
            },
            {
              field: 'kepMessageUuid',
              headerName: 'KEP UUID',
              hide: true,
            },
            {
              field: 'signedFileName',
              headerName: 'Podpísaný súbor',
              hide: true,
            },
            {
              field: '_search.deviceId',
              hide: true,
              filter: true,
            },
            {
              field: '_search.deviceName',
              headerName: 'Názov zariadenia',
              minWidth: 250,
              filter: true,
              floatingFilter: true,
              floatingFilterComponent: RevisionDeviceSelectFilter,
              cellStyle: { display: 'flex' },
              cellRenderer: DevicesColumn,
            },
            viewVariant === 'view-by-device'
              ? {
                  field: '_singleDevice.externalId',
                  headerName: 'Externé ID Zariadenia',
                  minWidth: 190,
                  sortable: true,
                  unSortIcon: true,
                  cellRenderer: SimpleValueFormatter,
                }
              : null,
            {
              field: '_search.devicesExport',
              headerName: viewVariant === 'view-by-revision' ? 'Názvy zariadení' : 'Názov zariadenia',
              hide: true,
            },
            ...indexValuesColumns,
            ...deviceEvidenceFieldsColumns,
            {
              field: '_actions',
              headerName: '',
              width: getResponsiveActionsColumnWidth(
                212 + (viewVariant === 'view-by-revision' ? 52 : 0),
                tableActionMode
              ),
              minWidth: getResponsiveActionsColumnWidth(
                212 + (viewVariant === 'view-by-revision' ? 52 : 0),
                tableActionMode
              ),
              maxWidth: getResponsiveActionsColumnWidth(
                212 + (viewVariant === 'view-by-revision' ? 52 : 0),
                tableActionMode
              ),
              pinned: 'right',
              cellStyle: { display: 'flex' },
              cellRenderer: ActionsColumn,
            },
          ] satisfies (ColDef | null)[]
        ).filter(Boolean) as ColDef[],
      [
        kind,
        viewVariant,
        isTablet,
        tableActionMode,
        indexValuesColumns,
        deviceEvidenceFieldsColumns,
        objectColumn.columns,
      ]
    ); // Filter out nulls for type compatibility

    const [defaultProfileFilters] = useState(() => ({
      'revisionStatus.revisionStatusId': {
        filter: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
      },
    }));

    const action = useCallback(async () => {
      const request = ++columnInventoryRequest.current;
      setLoadedColumnInventoryScope(null);
      const action = getAction('RevisionList');

      const filterName = kind === 'revision' ? 'kind.eq' : 'kind.ne';
      const revisions = await action({ query: { filters: { [filterName]: 'revision' } } });
      if (request !== columnInventoryRequest.current) {
        return [];
      }

      const indexFieldMap = new Map<string, string>();
      revisions.forEach(({ indexValues }) => {
        indexValues?.forEach(({ fieldName, fieldLabel }) => {
          if (!indexFieldMap.has(fieldName)) {
            indexFieldMap.set(fieldName, fieldLabel);
          }
        });
      });

      if (FEATURE_TOGGLE_REVISION_TABLE_INDEX_VALUES_COLUMNS) {
        setIndexValuesColumns(
          Array.from(indexFieldMap).map(([fieldName, fieldLabel]) => ({
            field: `indexValues.${fieldName}`,
            headerName: fieldLabel,
            minWidth: 200,
            sortable: true,
            unSortIcon: true,
            filter: true,
            floatingFilter: true,
            cellRenderer: ({ data }: { data: RevisionRow }) => {
              const value = data?.indexValues?.[fieldName];
              return <SimpleValueFormatter value={value} />;
            },
          }))
        );
      }

      const baseRows = revisions.map((revision) => ({
        ...revision,
        kindPretty: getKindPretty(revision.kind),
        indexValues:
          revision.indexValues?.reduce(
            (acc, { fieldName, value }) => ({ ...acc, [fieldName]: value }),
            {} as Record<string, string>
          ) ?? {},
        deviceSubtypes: {
          deviceTypeId: revision.deviceSubtype.map((deviceSubtype) => deviceSubtype.deviceTypeId).join(', '),
          deviceTypeName: revision.deviceSubtype.map((deviceSubtype) => deviceSubtype.deviceTypeName).join(', '),
        },
        deadlineStatus: revision.deadline
          ? isAfter(new Date(revision.deadline), new Date())
            ? 'before-deadline'
            : 'after-deadline'
          : 'no-deadline',
        safetyPointLabel: translateSafetyPoint(revision.safetyPoint, revision.safetyPointLabel),
        safetyPointPretty: translateSafetyPoint(revision.safetyPoint, revision.safetyPointLabel),
        _search: {
          deviceName: revision.devices.map(({ deviceName }) => deviceName).join(', '),
          deviceId: revision.devices.map(({ deviceId }) => deviceId).join(', '),
          devicesExport: revision.devices.map(({ deviceId, deviceName }) => `${deviceId}: ${deviceName}`).join('\n'),
        },
        deviceSubtypesString: revision.deviceSubtype.map((deviceSubtype) => deviceSubtype.deviceTypeName).join(', '),
      }));

      if (viewVariant === 'view-by-device') {
        const evidenceFieldDefMap = new Map<string, { name: string; precision: number }>();

        for (const revision of revisions) {
          for (const device of revision.devices) {
            for (const field of device.evidenceFields) {
              if (!field.evidenceFieldId) {
                continue;
              }

              const key = String(field.evidenceFieldId);

              if (!evidenceFieldDefMap.has(key)) {
                evidenceFieldDefMap.set(key, { name: field.fieldName ?? '', precision: field.precision ?? 0 });
              }
            }
          }
        }

        if (FEATURE_TOGGLE_REVISION_TABLE_EVIDENCE_FIELDS_COLUMNS) {
          setDeviceEvidenceFieldsColumns(
            Array.from(evidenceFieldDefMap).map(([key, { name, precision }]) => ({
              field: `_deviceEvidenceFields.${key}`,
              headerName: name,
              minWidth: 200,
              sortable: true,
              unSortIcon: true,
              filter: true,
              floatingFilter: true,
              cellRenderer: ({ data }: { data: RevisionRow }) => {
                const value = (data as any)?._deviceEvidenceFields?.[key];
                return <SimpleValueFormatter value={value} precision={precision} />;
              },
            }))
          );
        }

        const rows = baseRows
          .filter((row) => row.devices.length > 0)
          .flatMap((row) =>
            row.devices.map((device) => {
              const fields: Record<string, string | number | boolean> = {};

              for (const field of device.evidenceFields) {
                if (!field.evidenceFieldId) {
                  continue;
                }

                const key = String(field.evidenceFieldId);
                fields[key] = getEvidenceFieldValue(field) ?? '';
              }

              return {
                ...row,
                _singleDevice: device,
                _deviceEvidenceFields: fields,
                _search: {
                  deviceName: device.deviceName,
                  deviceId: String(device.deviceId),
                  devicesExport: `${device.deviceId}: ${device.deviceName}`,
                },
              };
            })
          );
        setLoadedColumnInventoryScope(columnInventoryScope);
        return rows;
      }

      setDeviceEvidenceFieldsColumns([]);
      setLoadedColumnInventoryScope(columnInventoryScope);
      return baseRows;
    }, [getAction, kind, viewVariant, columnInventoryScope]);

    /**
     * Handles the status change of a revision.
     */
    const onStatusChange = useCallback(
      ({ revisionId, status, technician }: OnStatusChangeParams) => {
        if (viewVariant === 'view-by-device') {
          api?.forEachNode((node) => {
            if (node.data?.revisionId === revisionId) {
              node.setData({ ...node.data, revisionStatus: status, technician: technician ?? node.data.technician });
            }
          });
        } else {
          const row = api?.getRowNode(String(revisionId));
          if (row && row.data) {
            row.setData({ ...row.data, revisionStatus: status, technician: technician ?? row.data.technician });
          }
        }
      },
      [api, viewVariant]
    );

    /**
     * Removes a row from the table when the user no longer has access to it.
     */
    const onNoLongerAccessible = useCallback(
      (revisionId: number) => {
        if (viewVariant === 'view-by-device') {
          const toRemove: RevisionRow[] = [];

          api?.forEachNode((node) => {
            if (node.data?.revisionId === revisionId) {
              toRemove.push(node.data);
            }
          });

          if (toRemove.length > 0) {
            api?.applyTransaction({ remove: toRemove });
          }
        } else {
          const row = api?.getRowNode(String(revisionId));

          if (row && row.data) {
            api?.applyTransaction({ remove: [row.data] });
          }
        }
      },
      [api, viewVariant]
    );

    const contextValue = useMemo(
      () => ({ onStatusChange, onNoLongerAccessible, viewVariant }),
      [onStatusChange, onNoLongerAccessible, viewVariant]
    );

    const addPermissionSlug = kind === 'revision' ? 'assign-revisions' : 'assign-forms';
    const tableIdSuffix = viewVariant === 'view-by-device' ? '-by-device' : '';
    const tableIdName = kind === 'revision' ? 'revisions' : 'forms';
    const tableId = `${tableIdName}${tableIdSuffix}`;

    return (
      <RevisionTableContext.Provider value={contextValue}>
        <DataTable
          ref={ref}
          key={columnInventoryScope} // Hydrate profiles again when the table or active identity changes.
          tableId={tableId}
          title={kind === 'revision' ? 'Revízne správy' : 'Formuláre'}
          addButtonText={kind === 'revision' ? 'Pridať novú' : 'Pridať nový'}
          hideToggleDiscarded
          afterTitle={viewToggle}
          dataRefreshToken={`${viewVariant}:${userId}`}
          getRowId={({ data }) =>
            data._singleDevice ? `${data.revisionId}-${data._singleDevice.deviceId}` : String(data.revisionId)
          }
          columns={columns}
          action={action}
          addButtonTarget={kind === 'revision' ? ADD_REVISION_PAGE_PATH.original : ADD_FORM_PAGE_PATH.original}
          initialFilters={initialFilters}
          defaultProfileFilters={defaultProfileFilters}
          hideAddButton={!hasPermissionAnywhere(addPermissionSlug)}
          selectedRows={selectedRows}
          onSelectedRowsChange={setSelectedRows}
          onReady={setApi}
          suppressSaveProfiles={suppressSaveProfiles}
          suppressProfileChangedNotification={suppressProfileChangedNotification}
          bulkActions={
            viewVariant === 'view-by-device' ? null : (
              <Group spacing={16}>
                <BulkViewAction revisions={selectedRows} />
                <BulkAcceptAction
                  revisions={selectedRows}
                  onStatusChange={onStatusChange}
                  onBulkActionComplete={() => api?.deselectAll()}
                />
                <BulkApproveAction
                  revisions={selectedRows}
                  onStatusChange={onStatusChange}
                  onBulkActionComplete={() => api?.deselectAll()}
                />
                <BulkRejectAction
                  revisions={selectedRows}
                  onStatusChange={onStatusChange}
                  onBulkActionComplete={() => api?.deselectAll()}
                />
                <BulkConfirmAction
                  revisions={selectedRows}
                  onStatusChange={onStatusChange}
                  onBulkActionComplete={() => api?.deselectAll()}
                />
                {FEATURE_TOGGLE_BULK_SIGN_REVISION && (
                  <BulkSignAction
                    revisions={selectedRows}
                    onStatusChange={onStatusChange}
                    onBulkActionComplete={() => api?.deselectAll()}
                  />
                )}
                <BulkDeleteAction
                  revisions={selectedRows}
                  onStatusChange={onStatusChange}
                  onBulkActionComplete={() => api?.deselectAll()}
                />
              </Group>
            )
          }
          profileColumnAliases={objectColumn.profileColumnAliases}
          profileMigrationReady={
            objectColumn.profileMigrationReady && loadedColumnInventoryScope === columnInventoryScope
          }
          dataExport={{
            modalTitle: kind === 'revision' ? 'Exportovať revízne správy' : 'Exportovať formuláre',
            fileName: kind === 'revision' ? 'revizne-spravy.xlsx' : 'formulare.xlsx',
            columnKeys: [
              'revisionId',
              'organization.organizationId',
              'organization.organizationName',
              ...objectColumn.columnKeys,
              'assignedBy.organizationId',
              'assignedBy.organizationName',
              'deviceType.deviceTypeId',
              'deviceType.deviceTypeName',
              'deviceSubtypesString',
              'revisionStatus.revisionStatusName',
              'revisionName',
              'technician.userId',
              'technician.fullName',
              'deadline',
              'safetyPointPretty',
              'finishedAt',
              'nextRevisionDate',
              'documentValidUntil',
              'kepMessageUuid',
              'signedFileName',
              '_search.devicesExport',
              ...(viewVariant === 'view-by-device' ? ['_singleDevice.externalId'] : []),
              'remoteSource.remoteSourceId',
              'remoteSource.name',
              'remoteId',
              'remoteUrl',
              ...indexValuesColumns.map(({ field }) => field ?? '').filter(Boolean),
              ...deviceEvidenceFieldsColumns.map(({ field }) => field ?? '').filter(Boolean),
            ],
            columnReplacements: {
              ...objectColumn.columnReplacements,
              deviceSubtypes: ['deviceSubtypesString'],
              'revisionStatus.revisionStatusId': ['revisionStatus.revisionStatusName'],
              '_search.deviceName': ['_search.devicesExport'],
            },
            ready: objectColumn.exportReady,
            error: objectColumn.exportError,
          }}
        />
      </RevisionTableContext.Provider>
    );
  }
);

interface IRevisionTableContext {
  onStatusChange: OnStatusChange;
  onNoLongerAccessible: OnNoLongerAccessible;
  viewVariant: 'view-by-revision' | 'view-by-device';
}

const RevisionTableContext = createContext<IRevisionTableContext>(undefined!);

/**
 * Table which displays revisions.
 */
const RevisionTable = forwardRef<IDataTablePublic, RevisionTableProps>((props, ref) => {
  return <RevisionTableImpl ref={ref} {...props} />;
});

export default RevisionTable;

/**
 * Hook to access the revision table context.
 */
export function useRevisionTableContext() {
  return useContext(RevisionTableContext);
}
